import { IAccount } from '@ViewModels';
import { css } from 'aphrodite';
import React from 'react';
import { useWatch } from 'react-hook-form';
import { IconTabTrigger, TabContent, TabList, TabRoot } from '../../../../components/Tabs';
import { EyeIcon } from '../../../../components/svgs/icons/EyeIcon';
import { ListOfQuestionsIcon } from '../../../../components/svgs/icons/ListOfQuestionsIcon';
import { TabSettingsIcon } from '../../../../components/svgs/icons/TabSettingsIcon';
import { usePreviewMode } from '../../shared/contexts/PreviewModeContext';
import { AdditionalQuestions } from '../../shared/components/AdditionalQuestions';
import { FieldStateManager } from '../../shared/components/AdditionalQuestions/types';
import { QuestionsWithSections } from '../../shared/components/QuestionsWithSections';
import { ReorderQuestionsButton } from '../../shared/components/ReorderQuestionsButton';
import { SurveySettings } from '../../shared/components/SurveySettings';
import { styleSheet } from '../styles';
import { TabValues } from '../utils';
import { BasicInfoSettings } from './BasicInfoSettings';
import { ConfirmationSettings } from './ConfirmationSettings';
import { DateRangeSettings } from './DateRangeSettings';
import { PreviewCustomSurveyForm } from './PreviewCustomSurveyForm';

interface SurveyTabsProps {
	selectedTab: TabValues;
	onTabChange: (value: TabValues) => void;
	settingsTabError: boolean;
	questionsTabError: boolean;
	surveyData?: any;
	account?: IAccount;
	isEdit: boolean;
	isClone: boolean;
	onReorderQuestions?: () => void;
	hasMultipleQuestions?: boolean;
	isLoading?: boolean;
	templateMode?: boolean;
	/** Field IDs whose conditional visibility was cleared by reordering */
	clearedVisibilityFieldIds?: string[];
	/** Field IDs that should be marked dirty due to reordering (position change or cleared visibility) */
	reorderDirtyFieldIds?: string[];
	/** Lifted per-field dirty manager so the container's save mutation can clear it after success */
	fieldStateManager?: FieldStateManager;
}

export const SurveyTabs: React.FC<SurveyTabsProps> = ({
	selectedTab,
	onTabChange,
	settingsTabError,
	questionsTabError,
	surveyData,
	account,
	isEdit,
	isClone,
	onReorderQuestions,
	hasMultipleQuestions = false,
	isLoading = false,
	templateMode,
	clearedVisibilityFieldIds,
	reorderDirtyFieldIds,
	fieldStateManager,
}) => {
	// Get preview mode from context
	const { isPreviewOnly } = usePreviewMode();
	const [reorderSlot, setReorderSlot] = React.useState<HTMLElement | null>(null);
	// V2 (sectioned) shape — gates the reorder slot so V1 never gets it.
	const surveyPartsForShape = useWatch({ name: 'surveyForm.surveyParts' }) as Array<{ _type?: string }> | undefined;
	const isV2 = Array.isArray(surveyPartsForShape) && surveyPartsForShape.some(p => p?._type === 'SurveySection');
	return (
		<TabRoot
			className={`tab-view ${css(styleSheet.tabViewContainer)}`}
			value={selectedTab}
			onValueChange={tValue => onTabChange(tValue as TabValues)}
		>
			<TabList styles={[styleSheet.tabList]}>
				<IconTabTrigger
					value={TabValues.Settings}
					icon={<TabSettingsIcon fillColor='currentColor' />}
					styles={settingsTabError ? [styleSheet.tabTextError] : []}
				>
					Settings
				</IconTabTrigger>
				<IconTabTrigger
					value={TabValues.Questions}
					icon={<ListOfQuestionsIcon fillColor='currentColor' />}
					styles={questionsTabError ? [styleSheet.tabTextError] : []}
				>
					Questions
				</IconTabTrigger>
				{surveyData?.anonymousLink || isPreviewOnly ? (
					<IconTabTrigger value={TabValues.Preview} icon={<EyeIcon fillColor='currentColor' />}>
						Preview
					</IconTabTrigger>
				) : null}
			</TabList>
			<TabContent
				forceMount
				value={TabValues.Settings}
				className={css(
					styleSheet.tabContent,
					styleSheet.newTabContent,
					selectedTab !== TabValues.Settings && styleSheet.tabContentHidden
				)}
			>
				<SurveySettings
					tabs={[
						{
							title: 'Basic Info',
							key: 'contact-fields',
							content: <BasicInfoSettings survey={surveyData} account={account} />,
						},
						{
							title: 'Date Range',
							key: 'date-range',
							content: <DateRangeSettings survey={surveyData} />,
						},
						{
							title: 'Confirmation',
							key: 'confirmation',
							content: <ConfirmationSettings survey={surveyData} />,
						},
					]}
				/>
			</TabContent>
			<TabContent
				forceMount
				value={TabValues.Questions}
				className={`scroll-view-items ${css(selectedTab !== TabValues.Questions && styleSheet.tabContentHidden)}`}
			>
				<div className={css(styleSheet.formContainer)}>
					<div className={css(styleSheet.bodyBackground)} />
					<div className={css(styleSheet.additionalQuestionsContainer)}>
						<div className={css(styleSheet.editorSideSpacer)} />
						<div className={css(styleSheet.editorContentColumn)}>
							<QuestionsTabContent
								isEdit={isEdit}
								isClone={isClone}
								templateMode={templateMode}
								fieldStateManager={fieldStateManager}
								clearedVisibilityFieldIds={clearedVisibilityFieldIds}
								reorderDirtyFieldIds={reorderDirtyFieldIds}
								reorderSlot={reorderSlot}
							/>
						</div>
						<div className={css(styleSheet.editorSideSpacer)}>
							{isV2 ? (
								<div ref={setReorderSlot} className={css(styleSheet.reorderSlot)} />
							) : (
								onReorderQuestions &&
								!isPreviewOnly && (
									<div className={css(styleSheet.reorderSlot)}>
										<ReorderQuestionsButton
											inline
											onReorderQuestions={onReorderQuestions}
											hasMultipleQuestions={hasMultipleQuestions}
											isLoading={isLoading}
										/>
									</div>
								)
							)}
						</div>
					</div>
				</div>
			</TabContent>
			{surveyData?.anonymousLink || isPreviewOnly ? (
				<TabContent value={TabValues.Preview} className={css(styleSheet.tabContent)}>
					<div className={css(styleSheet.formContainer)}>
						<PreviewCustomSurveyForm survey={surveyData} account={account} />
					</div>
				</TabContent>
			) : null}
		</TabRoot>
	);
};

interface QuestionsTabContentProps {
	isEdit: boolean;
	isClone?: boolean;
	templateMode?: boolean;
	clearedVisibilityFieldIds?: string[];
	reorderDirtyFieldIds?: string[];
	fieldStateManager?: FieldStateManager;
	reorderSlot?: HTMLElement | null;
}

/**
 * Renders V2 `<QuestionsWithSections />` when `surveyForm.surveyParts` contains any
 * `SurveySection`; otherwise renders the legacy V1 `<AdditionalQuestions />`.
 *
 * - New Custom Surveys are seeded with a V2 SurveySection in defaults (see `CustomSurvey/index.tsx`),
 *   so new surveys land on V2 immediately.
 * - Legacy surveys (no sections in their persisted shape) keep the V1 editor until the
 *   user clicks `+ Add Section` (V1's upgrade button — Task 15).
 */
const QuestionsTabContent: React.FC<QuestionsTabContentProps> = ({
	isEdit,
	isClone,
	templateMode,
	clearedVisibilityFieldIds,
	reorderDirtyFieldIds,
	fieldStateManager,
	reorderSlot,
}) => {
	const surveyParts = useWatch({ name: 'surveyForm.surveyParts' });
	const hasV2Shape = React.useMemo(
		() => Array.isArray(surveyParts) && surveyParts.some((p: { _type?: string }) => p?._type === 'SurveySection'),
		[surveyParts]
	);

	if (hasV2Shape) {
		return <QuestionsWithSections isEdit={isEdit} reorderSlot={reorderSlot} />;
	}

	return (
		<AdditionalQuestions
			isEdit={isEdit}
			isClone={isClone}
			templateMode={templateMode}
			clearedVisibilityFieldIds={clearedVisibilityFieldIds}
			reorderDirtyFieldIds={reorderDirtyFieldIds}
			fieldStateManager={fieldStateManager}
		/>
	);
};
